// modal-overlay.tsx — a full-viewport modal backdrop for the app's hand-rolled
// (non-Radix) modals, hardened against the campaign action-menu collision.
//
// THE BUG THIS EXISTS TO FIX
// --------------------------
// The campaign action menu (`campaign-row-menu.tsx`) is a Radix DropdownMenu in
// its default `modal` mode. When it is open at the same time as a hand-rolled
// modal (the reported case: `ScheduledCampaignEditModal`), it broke that modal
// two independent ways:
//
//   1) STACKING. The menu content is portaled to the app's shared portal
//      container (document.body in the web app) — the top-level stacking
//      context — at z-index 10000. A modal rendered INLINE in the app subtree
//      has its z-index confined to a nearer ancestor stacking context, so the
//      body-level menu paints OVER it no matter how large its z-index is.
//
//   2) POINTER EVENTS. A modal Radix menu mounts react-remove-scroll, which
//      injects a stylesheet setting `pointer-events: none` on document.body and
//      re-enables ONLY the menu content. A modal that is a body descendant but
//      outside that content inherits `none` and goes fully non-interactive while
//      the menu is open.
//
// ModalOverlay fixes both: it PORTALS into the same shared container the menu
// uses (so its z-index competes with the menu in one stacking context — and CSS
// vars still resolve inside the extension's shadow root, unlike a hard-coded
// document.body), and it CLAIMS `pointer-events: auto` so the body lock cannot
// freeze it.

import * as React from 'react';
import { createPortal } from 'react-dom';

import { usePortalContainer } from '@/common/hooks/use-portal-container';

/** Top-layer z-index — above the Radix menu's portaled content. */
export const MODAL_OVERLAY_Z = 2147483000;
/** The campaign action menu (Radix DropdownMenu) renders its content at this z. */
export const CAMPAIGN_MENU_Z = 10000;
/**
 * Z-index for a popover/calendar/dropdown opened from INSIDE a ModalOverlay
 * (FE#286). Those popovers portal to the SAME shared container as this overlay
 * (see usePortalContainer), so they share ONE stacking context with it — and at
 * their own default z-index (BTDatePicker/BTTimePicker: 20050; recurrence:
 * 91000) they land BELOW `MODAL_OVERLAY_Z` and render behind the modal, i.e. the
 * "date-change calendar doesn't open" report (it opened, invisibly). One above
 * the overlay puts them on top of it; still < the int32 ceiling (2147483647).
 */
export const MODAL_OVERLAY_POPOVER_Z = MODAL_OVERLAY_Z + 1;

export function modalOverlayStyle(zIndex: number = MODAL_OVERLAY_Z): React.CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    // A heavy scrim that clearly obscures the page behind the modal (the calendar
    // etc.). This is a literal (not a CSS var) so it always paints.
    background: 'rgba(0,0,0,.6)',
    display: 'grid',
    placeItems: 'center',
    zIndex,
    padding: 20,
    // Re-enable interaction even if a modal Radix menu has set
    // `pointer-events: none` on document.body via react-remove-scroll.
    pointerEvents: 'auto',
  };
}

/**
 * The design-token scope class. The redesign modals paint their frame with
 * `var(--bg)`/`var(--surface)`/`var(--line)`/`var(--ink)` etc., and those vars
 * are declared ONLY on `.campaigns-rd` (see styles/campaigns-redesign.css), not
 * on `:root`. Because ModalOverlay PORTALS its children out of the app subtree,
 * they land outside that wrapper and every token resolves to nothing → a
 * transparent frame that the page bleeds through (the D12 "no opaque backdrop"
 * bug, reproduced in BOTH the web app and the extension). Re-applying the scope
 * class on the portaled backdrop restores the tokens for the whole modal.
 */
export const REDESIGN_SCOPE_CLASS = 'campaigns-rd';

/**
 * Copy the active `data-theme` (light/dark, driven by `waDark`) from the app's
 * existing `.campaigns-rd` wrapper so the portaled modal themes identically. We
 * look in the portal container's own root (document, or the extension shadow
 * root the container lives in) so it works in both surfaces. Absent → the base
 * `.campaigns-rd` rule (light) applies.
 */
export function resolveRedesignTheme(
  container: ShadowRoot | HTMLElement | null | undefined,
): string | undefined {
  try {
    const root: Document | ShadowRoot =
      (container && (container.getRootNode() as Document | ShadowRoot)) || document;
    const scoped = root.querySelector?.('.campaigns-rd[data-theme]') as HTMLElement | null;
    return scoped?.getAttribute('data-theme') || undefined;
  } catch {
    return undefined;
  }
}

export interface ModalOverlayProps {
  /** Fires on a backdrop click (children should stopPropagation). */
  onClose?: () => void;
  /** Override the default top-layer z-index if a specific stack is needed. */
  zIndex?: number;
  children: React.ReactNode;
}

export const ModalOverlay: React.FC<ModalOverlayProps> = ({ onClose, zIndex, children }) => {
  const container = usePortalContainer();
  // Computed inline (no extra hook) — a DOM read this cheap on a modal that mounts
  // rarely doesn't need memoizing, and it keeps ModalOverlay's hook surface identical.
  const dataTheme = resolveRedesignTheme(container);
  return createPortal(
    // `campaigns-rd` re-declares the design tokens the portaled modal needs; the
    // copied `data-theme` keeps light/dark identical to the app behind it.
    <div
      className={REDESIGN_SCOPE_CLASS}
      data-theme={dataTheme}
      onClick={onClose}
      style={modalOverlayStyle(zIndex)}
    >
      {children}
    </div>,
    container,
  );
};

export default ModalOverlay;
